import { createRef } from "react";

import type {
	Status,
	Recording,
	SetRecording,
	RecordingError,
	RecordingStore,
} from "../../types/components/webcam/useRecordingStoreTypes";
import { defaultCodec } from "./codec";
import { createStore, useStore } from "./store";

export const ERROR_MESSAGES = {
	CODEC_NOT_SUPPORTED: "CODEC_NOT_SUPPORTED",
	SESSION_EXISTS: "SESSION_EXISTS",
	NO_RECORDING_WITH_ID: "NO_RECORDING_WITH_ID",
	NO_USER_PERMISSION: "NO_USER_PERMISSION",
} as const;

export const STATUS = {
	INITIAL: "INITIAL",
	CLOSED: "CLOSED",
	OPEN: "OPEN",
	RECORDING: "RECORDING",
	STOPPED: "STOPPED",
	ERROR: "ERROR",
	PAUSED: "PAUSED",
} as const;

export function createRecording({ videoId, audioId, videoLabel, audioLabel }: SetRecording): Recording {
	const recordingId = `${videoId}-${audioId}`;

	const recording: Recording = {
		id: recordingId,
		audioId,
		audioLabel,
		blobChunks: [],
		fileName: String(new Date().getTime()),
		fileType: "webm",
		isMuted: false,
		mimeType: defaultCodec,
		objectURL: null,
		previewRef: createRef(),
		recorder: null,
		status: STATUS.INITIAL,
		videoId,
		videoLabel,
		webcamRef: createRef(),
	};
	return recording;
}

const recordingMap: Map<string, Recording> = new Map();
const store = createStore(recordingMap);

export function useRecordingStore(): RecordingStore {
	const { state } = useStore(store);
	const activeRecordings = Array.from(recordingMap?.values?.());

	const clearAllRecordings = async (): Promise<void> => {
		Array.from(state.values()).forEach((recording) => {
			const stream = <MediaStream>recording.webcamRef.current?.srcObject;

			if (stream) {
				stream.getTracks().forEach((track) => track.stop());
			}
		});
		state.clear(true);
	};

	const isRecordingCreated = (recordingId: string): boolean => {
		const isCreated = state.get(recordingId);
		return Boolean(isCreated);
	};

	const getRecording = (recordingId: string): Recording => {
		const recording = state.get(recordingId);
		if (!recording) {
			throw new Error(ERROR_MESSAGES.NO_RECORDING_WITH_ID);
		}
		return recording;
	};

	const setRecording = async (params: SetRecording): Promise<Recording> => {
		const recording = createRecording(params);
		const newRecording = state.set(recording.id, recording, true);
		return newRecording;
	};

	const updateRecording = async (recordingId: string, updatedValues: Partial<Recording>): Promise<Recording> => {
		const recording = <Recording>state.get(recordingId);
		const updatedRecording = state.set(
			recordingId,
			{
				...recording,
				...updatedValues,
			},
			true,
		);
		return updatedRecording;
	};

	const deleteRecording = async (recordingId: string): Promise<void> => {
		state.delete(recordingId, true);
	};

	return {
		activeRecordings,
		clearAllRecordings,
		deleteRecording,
		getRecording,
		isRecordingCreated,
		setRecording,
		updateRecording,
	};
}
